import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Loader2, Mail, MessageSquare, MinusCircle, XCircle,
} from "lucide-react";
import toast from "react-hot-toast";

import { getMarketUpdatePreview, sendPropertyMarketUpdate } from "../api/prospectingReportsApi";
import { formatMelbourneShortDate } from "../../../utils/melbourneTime";
import { MU_RED, inputClass, outlineButtonClass, redButtonClass, vendorName } from "../marketUpdateTheme";

const RESULT_ICON = {
  sent: <CheckCircle2 className="w-4 h-4 text-[#1f7a46]" aria-hidden="true" />,
  skipped: <MinusCircle className="w-4 h-4 text-[#9a5b00]" aria-hidden="true" />,
  failed: <XCircle className="w-4 h-4 text-[#b42318]" aria-hidden="true" />,
};
const RESULT_LABEL = { sent: "Sent", skipped: "Not sent", failed: "Failed" };

const localMobile = (digits) => (digits && /^61\d{9}$/.test(digits)
  ? `0${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}` : digits);

const ChannelCard = ({ icon: Icon, title, checked, onToggle, disabledReason, children }) => (
  <section className={`rounded-xl border bg-white ${checked ? "border-gray-300" : "border-gray-200"}`}>
    <label className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer">
      <input type="checkbox" className="rounded border-gray-300" checked={checked} onChange={onToggle} disabled={!!disabledReason} />
      <Icon className="w-4 h-4 text-gray-600" aria-hidden="true" />
      <span className="text-sm font-bold text-gray-900">{title}</span>
      {disabledReason && <span className="ml-auto text-xs text-gray-500">{disabledReason}</span>}
    </label>
    <div className={`p-4 ${checked ? "" : "opacity-50 pointer-events-none"}`}>{children}</div>
  </section>
);

/**
 * "Have it where agents can review SMS & Email to send off and then click send."
 *
 * Nothing goes out until Send is clicked. The server still refuses an
 * unsubscribed address, and anybody who got a market update in the last two
 * weeks, unless the agent chooses "Send anyway" for them.
 */
const MarketUpdateSendStep = ({ report, onSent, onBack, onDone }) => {
  const vendors = report.vendors || [];
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState(() => vendors.map((v) => !!(v.mobile || v.email)));
  const [useSms, setUseSms] = useState(vendors.some((v) => v.mobile));
  const [useEmail, setUseEmail] = useState(vendors.some((v) => v.email));
  const [smsText, setSmsText] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    getMarketUpdatePreview(report.id, { withSender: true })
      .then(({ data }) => {
        setPreview(data);
        setSmsText(data.sms_template || "");
        setSubject(data.subject || "");
      })
      .catch(() => toast.error("Could not load the messages."));
  }, [report.id]);

  const chosen = vendors.map((v, i) => ({ ...v, index: i })).filter((v) => picked[v.index]);
  const smsCount = useSms ? chosen.filter((v) => v.mobile).length : 0;
  const emailCount = useEmail ? chosen.filter((v) => v.email).length : 0;
  const total = smsCount + emailCount;
  const alreadySent = report.status === "sent";

  const smsPreview = useMemo(() => {
    const first = chosen.find((v) => v.mobile)?.first_name || "there";
    return `${(smsText || "").replace(/\{first_name\}/g, first)}\n\n${preview?.share_url || ""}`;
  }, [smsText, chosen, preview]);

  // `retry` is "Send anyway": only the people the repeat check skipped, and only
  // by the channel they were skipped on, so nobody who was just sent to gets it twice.
  const send = async (retry = null) => {
    if (!total) {
      toast.error("Pick at least one person and one way to reach them.");
      return;
    }
    const question = retry
      ? `Send anyway to ${retry.map((r) => r.name || r.to).join(", ")}? They had a market update in the last ${preview.repeat_window_days} days.`
      : `Send ${[smsCount && `${smsCount} SMS`, emailCount && `${emailCount} email${emailCount === 1 ? "" : "s"}`].filter(Boolean).join(" and ")} to ${chosen.map((v) => vendorName(v) || v.mobile || v.email).join(", ")}?`;
    if (!window.confirm(question)) return;

    setSending(true);
    try {
      const { data } = await sendPropertyMarketUpdate(report.id, {
        channels: retry ? [...new Set(retry.map((r) => r.channel))] : [useSms && "sms", useEmail && "email"].filter(Boolean),
        vendor_indexes: chosen.map((v) => v.index),
        sms_message: smsText,
        subject,
        resend: alreadySent || !!retry,
        force: !!retry,
        ...(retry ? { only_recipients: retry.map((r) => r.to) } : {}),
      });
      setOutcome(data);
      onSent(data.report);
      if (data.sent) toast.success(`Sent ${data.sent} message${data.sent === 1 ? "" : "s"}.`);
      else toast.error("Nothing was sent. See why below.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not send the market update.");
    } finally {
      setSending(false);
    }
  };

  if (!preview) {
    return <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  if (outcome) {
    const repeatSkips = outcome.results.filter((r) => r.result === "skipped" && r.reason.startsWith("Already"));
    return (
      <div className="max-w-2xl mx-auto">
        <section className="rounded-xl border border-gray-200 bg-white">
          <h2 className="px-5 py-4 border-b border-gray-100 text-base font-bold text-gray-900">
            {outcome.sent ? `${outcome.sent} message${outcome.sent === 1 ? "" : "s"} sent` : "Nothing was sent"}
          </h2>
          <ul className="divide-y divide-gray-100">
            {outcome.results.map((r, i) => (
              <li key={i} className="flex items-start gap-3 px-5 py-3">
                <span className="mt-0.5">{RESULT_ICON[r.result]}</span>
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-semibold">{r.channel === "sms" ? "SMS" : "Email"}</span> to {r.name || r.to}
                    <span className="text-gray-500"> · {r.to}</span>
                  </p>
                  <p className="text-xs text-gray-600">{RESULT_LABEL[r.result]}{r.reason ? `: ${r.reason}` : ""}</p>
                </div>
              </li>
            ))}
          </ul>
          {outcome.sent > 0 && smsCount > 0 && (
            <p className="px-5 py-3 border-t border-gray-100 text-xs text-gray-600">
              Texts were sent from {outcome.sms_sender === "COLLINGS" ? "COLLINGS" : `your mobile ${localMobile(outcome.sms_sender)}`}.
            </p>
          )}
        </section>
        <div className="flex flex-wrap justify-end gap-2 mt-5">
          {repeatSkips.length > 0 && (
            <button type="button" onClick={() => send(repeatSkips)} disabled={sending} className={`${outlineButtonClass} px-4 py-2.5`}>
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              Send anyway to the {repeatSkips.length} skipped
            </button>
          )}
          <button type="button" onClick={onDone} className={`${redButtonClass} px-6 py-2.5`} style={{ backgroundColor: MU_RED }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  const ownNumber = preview.sms_sender_is_own;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-bold text-gray-900 mb-3">Recipients</h2>
        <ul className="space-y-2">
          {vendors.map((v, i) => {
            const reachable = v.mobile || v.email;
            return (
              <li key={i}>
                <label className={`flex items-start gap-3 ${reachable ? "cursor-pointer" : "opacity-60"}`}>
                  <input type="checkbox" className="mt-0.5 rounded border-gray-300" disabled={!reachable}
                    checked={!!picked[i]} onChange={(e) => setPicked((prev) => prev.map((p, j) => (j === i ? e.target.checked : p)))} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900">{vendorName(v) || "Unnamed vendor"}</span>
                    <span className="block text-xs text-gray-600">
                      {reachable ? [v.mobile, v.email].filter(Boolean).join(" · ") : "No mobile or email. Add one on the Vendor step."}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </section>

      <ChannelCard icon={MessageSquare} title="SMS" checked={useSms} onToggle={() => setUseSms((v) => !v)}
        disabledReason={vendors.some((v) => v.mobile) ? "" : "No mobile numbers"}>
        <label htmlFor="mu-sms" className="block text-xs font-semibold text-gray-700 mb-1.5">Edit message</label>
        <textarea id="mu-sms" rows={4} value={smsText} onChange={(e) => setSmsText(e.target.value)} className={inputClass} />
        <p className="text-xs text-gray-500 mt-1">{"{first_name}"} is replaced with each vendor's first name.</p>
        <div className="mt-3 rounded-lg bg-gray-50 border border-gray-100 p-3">
          <p className="text-[11px] font-semibold text-gray-500 mb-1">What they will see</p>
          <p className="text-sm text-gray-800 whitespace-pre-line break-words">{smsPreview}</p>
          <p className="text-[11px] text-gray-500 mt-2">An unsubscribe link is added to the end automatically.</p>
        </div>
        <p className={`flex items-start gap-1.5 text-xs mt-3 ${ownNumber ? "text-gray-600" : "text-[#7a4b00]"}`}>
          {!ownNumber && <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0" aria-hidden="true" />}
          {ownNumber
            ? `Sent from your mobile, ${localMobile(preview.sms_sender)}. Replies go straight to your phone.`
            : "Sent from COLLINGS. Your own mobile is used once it is verified in Settings > SMS Senders."}
        </p>
      </ChannelCard>

      <ChannelCard icon={Mail} title="Email" checked={useEmail} onToggle={() => setUseEmail((v) => !v)}
        disabledReason={vendors.some((v) => v.email) ? "" : "No email addresses"}>
        <label htmlFor="mu-subject" className="block text-xs font-semibold text-gray-700 mb-1.5">Subject</label>
        <input id="mu-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass} />
        <p className="text-xs text-gray-500 mt-2">
          The email is the report from the previous screen, from {report.agent?.name || "you"} (Collings Real Estate), with
          an unsubscribe link at the bottom. Replies come to your inbox.
        </p>
      </ChannelCard>

      {preview.sends?.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-bold text-gray-900 mb-2">Already sent from this report</h2>
          <ul className="space-y-1">
            {preview.sends.map((s) => (
              <li key={s.id} className="flex items-center gap-2 text-xs text-gray-700">
                {RESULT_ICON[s.result]}
                <span>{s.channel === "sms" ? "SMS" : "Email"} to {s.recipient_name || s.recipient}</span>
                <span className="text-gray-500 tabular-nums">· {formatMelbourneShortDate(s.created_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="text-xs text-gray-600 leading-relaxed">
        <h2 className="text-sm font-bold text-gray-900 mb-1">Before you send</h2>
        <p>By sending, you confirm that you:</p>
        <ol className="list-decimal pl-5 mt-1 space-y-0.5">
          <li>have consent to send marketing messages to these people;</li>
          <li>are clearly identified as the sender, with Collings Real Estate; and</li>
          <li>are giving them a way to unsubscribe (added automatically).</li>
        </ol>
        <p className="mt-2">
          Anyone who has unsubscribed, or who got a market update in the last {preview.repeat_window_days} days, is skipped.
        </p>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" onClick={onBack} className={`${outlineButtonClass} px-4 py-2.5`}>Back to preview</button>
        <button type="button" onClick={() => send()} disabled={sending || !total}
          className={`${redButtonClass} px-6 py-2.5`} style={{ backgroundColor: MU_RED }}>
          {sending && <Loader2 className="w-4 h-4 animate-spin" />}
          {alreadySent ? "Send again" : "Send"}{total ? ` (${total})` : ""}
        </button>
      </div>
    </div>
  );
};

export default MarketUpdateSendStep;
